import type { ZudokuConfig } from "zudoku";
import baseConfig from "./docs.zudoku.config";
import { cookieConsentConfig } from "../../cookie-consent.mjs";
import enterpriseNavigation from "./enterprise-navigation.json";

const POSTHOG_KEY = process.env.ZUDOKU_PUBLIC_POSTHOG_KEY ?? "";
const POSTHOG_HOST = process.env.ZUDOKU_PUBLIC_POSTHOG_HOST ?? "";

// When ZUDOKU_PUBLIC_OIDC_ISSUER_URL is unset, build the docs without OIDC
// auth and leave `/enterprise/*` routes unprotected.  This matches the
// Caddyfile behaviour: no OIDC vars means the enterprise content is served
// open for local dev.
//
// The docs are built with `zudoku build --experimental-ssr` (see the
// Dockerfile): with `protectedRoutes` set, the chunks holding the
// `/enterprise/*` page content are moved out of the public output and only
// served by the SSR server, and every page render checks the session cookie
// server-side before including protected content.
//
// The `ZUDOKU_PUBLIC_` prefix is required: zudoku's config-file Vite plugin
// only inlines env vars whose names start with one of its allowed prefixes
// (`ZUPLO_PUBLIC_` or `ZUDOKU_PUBLIC_`).  Unprefixed `process.env.*`
// references in this file are rewritten to the literal token `undefined` in
// the SPA bundle, which would make this gate always evaluate to "disabled"
// at runtime regardless of the Node-side value.
const OIDC_ISSUER_URL = process.env.ZUDOKU_PUBLIC_OIDC_ISSUER_URL ?? "";
const OIDC_ENABLED = OIDC_ISSUER_URL !== "";

type NavigationItem = NonNullable<ZudokuConfig["navigation"]>[number];

// Hide the Enterprise category and each entry of its sidebar from users who
// lack the enterprise entitlement.  This is a UX hint only; access is
// enforced by the protectedRoutes check below and by Caddy.  The entries
// need it too: on an /enterprise page the sidebar lists the category's
// entries whether the category itself is shown or not, which would put the
// page titles on the sign-in page.  When OIDC is disabled nothing is
// hidden, matching the open Caddy config.
//
// Note: zudoku serializes the navigation config for SSR, so this callback
// must be self-contained — it cannot reference helpers defined in outer
// scope (they are undefined at SSR time).
const enterpriseOnly = OIDC_ENABLED && {
  display: ({
    auth,
  }: {
    auth: { isAuthenticated: boolean; profile: object | null };
  }) => {
    if (!auth.isAuthenticated) return false;
    // The zudoku patch adds the token's `scope` claim to the profile.
    const scope = (auth.profile as { scope?: unknown } | null)?.scope;
    return (
      typeof scope === "string" && scope.split(" ").includes("docs:enterprise")
    );
  },
};

// Sidebar groups of the enterprise pages, from the enterprise docs repo's
// navigation.json, which the Dockerfile copies over this empty placeholder.
// Rendered like the upstream docs sidebar: nested categories, expanded.
const enterpriseGroups = (
  enterpriseNavigation as Array<{ label: string; icon?: string; items: string[] }>
).map(
  (group) =>
    ({
      type: "category",
      label: group.label,
      icon: group.icon,
      collapsed: false,
      items: group.items.map((slug) => `/enterprise/${slug}`),
      ...enterpriseOnly,
    }) as NavigationItem,
);

const config: ZudokuConfig = {
  ...baseConfig,
  ...(OIDC_ENABLED && {
    authentication: {
      type: "openid" as const,
      clientId: "unikraft-docs",
      issuer: OIDC_ISSUER_URL,
      scopes: [
        "openid",
        "profile",
        "email",
        "offline_access",
        "docs:enterprise",
      ],
      disableSignUp: true,
      // This allows insecure HTTP requests to the OIDC provider,
      // which is required for the local docker compose setup
      allowInsecureRequests: OIDC_ISSUER_URL.startsWith("http://"),
    },
    // Enforced in three places that all read the access token's `scope`
    // claim: this check runs on the SSR server (and again in the browser)
    // and decides whether a render includes the page content and whether
    // the browser requests the page chunk at all; Caddy validates the JWT in
    // the session cookie before proxying /docs/_protected/* chunks or
    // serving /docs/_enterprise-data/* images and the PDF; and the zudoku
    // patch (apps/docs/patches/zudoku.patch) surfaces the claim on
    // `profile.scope` for both the server-side and client-side profile.
    protectedRoutes: {
      "/enterprise/*": ({ auth, reasonCode }) => {
        if (!auth.isAuthenticated) return reasonCode.UNAUTHORIZED;
        const scope = auth.profile?.scope;
        return typeof scope === "string" &&
          scope.split(" ").includes("docs:enterprise")
          ? true
          : reasonCode.FORBIDDEN;
      },
    },
  }),
  navigation: [
    ...(baseConfig.navigation ?? []),
    {
      type: "category",
      label: "Enterprise",
      icon: "lock",
      ...enterpriseOnly,
      // The overview is owned here (apps/docs/enterprise-pages); the groups
      // come from the enterprise repo. Labels and icons are in each page's
      // frontmatter.
      items: [
        { type: "doc", file: "/enterprise/overview", ...enterpriseOnly },
        ...enterpriseGroups,
      ],
    },
  ],
  search: {
    type: "pagefind",
    // Enterprise results come from the separate pagefind-protected index,
    // which the search client only merges in when the user can read it
    // (zudoku patch, Caddy gate). Filtering here as well keeps them out if
    // the user signs out without a reload.
    transformResults: ({ result, auth }) => {
      if (!result.url.includes("/enterprise/")) return true; // leave non-enterprise results alone

      if (!auth.isAuthenticated) return false;
      const scope = auth.profile?.scope;
      return typeof scope === "string" && scope.split(" ").includes("docs:enterprise")
        ? true
        : false;
    },
  },
  plugins: [
    ...(baseConfig.plugins ?? []),
    {
      getHead: () => (
        <>
          <link
            rel="stylesheet"
            href="https://cdn.jsdelivr.net/gh/orestbida/cookieconsent@3.1.0/dist/cookieconsent.css"
          />
          <script
            defer
            src="https://cdn.jsdelivr.net/gh/orestbida/cookieconsent@3.1.0/dist/cookieconsent.umd.js"
          ></script>
          <script>{`
            document.addEventListener('DOMContentLoaded', function () {
              if (typeof CookieConsent === 'undefined') return;
              CookieConsent.run(${JSON.stringify(cookieConsentConfig)});
            });
          `}</script>
          <script>{`
            window.addEventListener('cc:onConsent', function (event) {
              var hasAnalyticsConsent = event.detail && event.detail.cookie && event.detail.cookie.categories && event.detail.cookie.categories.indexOf('analytics') !== -1;
              if (!hasAnalyticsConsent) return;
              if (!document.querySelector('script[data-analytics="ga-gtag"]')) {
                var gaScript = document.createElement('script');
                gaScript.async = true;
                gaScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-3YQ9TL8CWJ';
                gaScript.setAttribute('data-analytics', 'ga-gtag');
                document.head.appendChild(gaScript);
              }
              window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'G-3YQ9TL8CWJ'); gtag('config', 'AW-17059089946');
              (function (h, o, t, j, a, r) {
                h.hj =
                  h.hj ||
                  function () {
                    (h.hj.q = h.hj.q || []).push(arguments);
                  };
                h._hjSettings = { hjid: 3896733, hjsv: 6 };
                a = o.getElementsByTagName('head')[0];
                r = o.createElement('script');
                r.async = 1;
                r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
                a.appendChild(r);
              })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
              (function (l) {
                if (!l) {
                  window.lintrk = function (a, b) {
                    window.lintrk.q.push([a, b]);
                  };
                  window.lintrk.q = [];
                }
                var s = document.getElementsByTagName('head')[0];
                var b = document.createElement('script');
                b.type = 'text/javascript';
                b.async = true;
                b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
                s.parentNode.insertBefore(b, s);
              })(window.lintrk);
              (function () {
                var s = document.getElementsByTagName('head')[0];
                var b = document.createElement('script');
                b.type = 'text/javascript';
                b.async = true;
                b.src = '//js-eu1.hs-scripts.com/139568528.js';
                s.parentNode.insertBefore(b, s);
              })();
              !(function (t) {
                if (window.ko) return;
                ((window.ko = []),
                  ['identify', 'track', 'removeListeners', 'open', 'on', 'off', 'qualify', 'ready'].forEach(
                    function (t) {
                      ko[t] = function () {
                        var n = [].slice.call(arguments);
                        return (n.unshift(t), ko.push(n), ko);
                      };
                    }
                  ));
                var n = document.createElement('script');
                ((n.async = !0),
                  n.setAttribute(
                    'src',
                    'https://cdn.getkoala.com/v1/pk_ffc2b4347a28efcf410dec86cadd8cef02f7/sdk.js'
                  ),
                  (document.body || document.head).appendChild(n));
              })();
              (function () {
                if (typeof window === 'undefined') return;
                if (typeof window.signals !== 'undefined') return;
                var script = document.createElement('script');
                script.src =
                  'https://cdn.cr-relay.com/v1/site/2c9b8ced-9989-46b3-a43b-53300a399dd5/signals.js';
                script.async = true;
                window.signals = Object.assign(
                  [],
                  ['page', 'identify', 'form'].reduce(function (acc, method) {
                    acc[method] = function () {
                      signals.push([method, arguments]);
                      return signals;
                    };
                    return acc;
                  }, {})
                );
                document.head.appendChild(script);
              })();
              !(function () {
                var e, t, n;
                ((e = '29161580139587d'),
                  (t = function () {
                    Reo.init({ clientID: '29161580139587d' });
                  }),
                  ((n = document.createElement('script')).src = 'https://static.reo.dev/' + e + '/reo.js'),
                  (n.defer = !0),
                  (n.onload = t),
                  document.head.appendChild(n));
              })();
              !(function (key) {
                if (window.reb2b) return;
                window.reb2b = { loaded: true };
                var s = document.createElement('script');
                s.async = true;
                s.src = 'https://ddwl4m2hdecbv.cloudfront.net/b/' + key + '/' + key + '.js.gz';
                document
                  .getElementsByTagName('script')[0]
                  .parentNode.insertBefore(s, document.getElementsByTagName('script')[0]);
              })('4N210H5PRQ6Z');
              var posthogKey = '${POSTHOG_KEY}';
              var posthogHost = '${POSTHOG_HOST}';
              if (!posthogKey || !posthogHost || window.__posthog_initialized) return;
              window.__posthog_initialized = true;
              !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys getNextSurveyStep onSessionId".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
              posthog.init(posthogKey, {
                api_host: posthogHost,
                defaults: '2025-11-30',
                capture_pageview: 'history_change'
              });
            });
          `}</script>
        </>
      ),
    },
  ],
};

export default config;
